/**
 * The API surface — every backend call in this bundle goes through here.
 *
 *   import { api } from "../api";
 *   await api.blockedWords.remove({ wordId });
 *
 * Types are fully inferred from the server's AppRouter, so procedure paths,
 * inputs and outputs are all statically checked.
 *
 * This file used to be `api/trpc.ts` behind a one-line `api/index.ts` re-export
 * — a seam from when calls could route to either tRPC or the PHP fallback. With
 * one implementation left the indirection earned nothing, so they were merged.
 */

import { raft } from "../lib/server";
import type { ProcedurePath, InputFor } from "../lib/server";

/** Create a typed mutation wrapper for a tRPC procedure */
function m<T extends ProcedurePath>(path: T) {
  return (input: InputFor<T>) => raft.mutate(path, input);
}

/** Create a typed query wrapper for a tRPC procedure */
function q<T extends ProcedurePath>(path: T) {
  return (input: InputFor<T>) => raft.query(path, input);
}

export const api = {
  // ── admin ───────────────────────────────────────────────────────
  admin: {
    dashboard: q("admin.dashboard"),
    overviewCharts: q("admin.overviewCharts"),
  },

  // ── dashboard tabs (fetched tab bodies) ─────────────────────────
  dashboardTab: {
    load: q("dashboardTab.load"),
    followed: q("dashboardTab.followed"),
    artists: q("dashboardTab.artists"),
    artistStats: q("dashboardTab.artistStats"),
    resellers: q("dashboardTab.resellers"),
    expired: q("dashboardTab.expired"),
    resellerSalesOverview: q("dashboardTab.resellerSalesOverview"),
  },

  // ── advance ─────────────────────────────────────────────────────
  advance: {
    add: m("advance.add"),
    addAdvance: q("advance.addAdvance"),
    delete: m("advance.delete"),
  },

  // ── agreements ──────────────────────────────────────────────────
  agreements: {
    getAll: q("agreements.getAll"),
  },

  // ── apiKeys ─────────────────────────────────────────────────────
  apiKeys: {
    generate: m("apiKeys.generate"),
  },

  // ── artists ─────────────────────────────────────────────────────
  artists: {
    acceptUploadRules: m("artists.acceptUploadRules"),
    activate: m("artists.activate"),
    addFeatured: m("artists.addFeatured"),
    addFeaturedArtist: q("artists.addFeaturedArtist"),
    artistDetails: q("artists.artistDetails"),
    artistFeatures: q("artists.artistFeatures"),
    biography: q("artists.biography"),
    blockUserFromPartners: q("artists.blockUserFromPartners"),
    bioIgnore: m("artists.bioIgnore"),
    blockUpload: m("artists.blockUpload"),
    dashboard: q("artists.dashboard"),
    deleteFeatured: m("artists.deleteFeatured"),
    feature: m("artists.feature"),
    getNotes: q("artists.getNotes"),
    hideAll: m("artists.hideAll"),
    saveAltDesc: m("artists.saveAltDesc"),
    saveAltName: m("artists.saveAltName"),
    saveBio: m("artists.saveBio"),
    saveCollection: m("artists.saveCollection"),
    saveExpandedProducts: m("artists.saveExpandedProducts"),
    saveExtEarn: m("artists.saveExtEarn"),
    saveExtras: m("artists.saveExtras"),
    saveFeaturedOrder: m("artists.saveFeaturedOrder"),
    saveGreetName: m("artists.saveGreetName"),
    saveHide: m("artists.saveHide"),
    saveLicenseType: m("artists.saveLicenseType"),
    saveNotes: m("artists.saveNotes"),
    savePricing: m("artists.savePricing"),
    saveQuota: m("artists.saveQuota"),
    saveUserBio: m("artists.saveUserBio"),
    saveUsername: m("artists.saveUsername"),
    setThumb: m("artists.setThumb"),
    switchBlockMode: m("artists.switchBlockMode"),
  },

  // ── artwork ─────────────────────────────────────────────────────
  artwork: {
    alternative: q("artwork.alternative"),
    artworkCollections: q("artwork.artworkCollections"),
    aiMetadata: m("artwork.aiMetadata"),
    artworks: q("artwork.artworks"),
    delete: m("artwork.delete"),
    details: q("artwork.details"),
    directory: q("artwork.directory"),
    directoryEdit: q("artwork.directoryEdit"),
    directoryEditRefresh: q("artwork.directoryEditRefresh"),
    directoryRefresh: q("artwork.directoryRefresh"),
    directoryUpdate: m("artwork.directoryUpdate"),
    directoryUploadComplete: m("artwork.directoryUploadComplete"),
    directoryUploadPresign: m("artwork.directoryUploadPresign"),
    editPhoto: q("artwork.editPhoto"),
    hideDup: m("artwork.hideDup"),
    markAsBad: m("artwork.markAsBad"),
    markAsStretch: m("artwork.markAsStretch"),
    multiDeleteTag: m("artwork.multiDeleteTag"),
    repairImagesList: q("artwork.repairImagesList"),
    repairImagesOne: m("artwork.repairImagesOne"),
    multiTag: m("artwork.multiTag"),
    optin: m("artwork.optin"),
    preview: q("artwork.preview"),
    pricing: q("artwork.pricing"),
    reupload: q("artwork.reupload"),
    reuploadComplete: m("artwork.reuploadComplete"),
    reuploadPresign: m("artwork.reuploadPresign"),
    saveArtwork: m("artwork.saveArtwork"),
    saveCategory: m("artwork.saveCategory"),
    saveMultiMeta: m("artwork.saveMultiMeta"),
    saveTitle: m("artwork.saveTitle"),
    view: q("artwork.view"),
  },

  // ── auth ────────────────────────────────────────────────────────
  auth: {
    forgotPassword: m("auth.forgotPassword"),
    login: m("auth.login"),
    resetPassword: m("auth.resetPassword"),
    signout: m("auth.signout"),
    signup: m("auth.signup"),
    signupValidate: m("auth.signupValidate"),
  },

  // ── blockedWords ────────────────────────────────────────────────
  blockedWords: {
    add: m("blockedWords.add"),
    blockwords: q("blockedWords.blockwords"),
    list: q("blockedWords.list"),
    remove: m("blockedWords.remove"),
  },

  // ── briefs ──────────────────────────────────────────────────────
  briefs: {
    add: m("briefs.add"),
    briefCollection: q("briefs.briefCollection"),
    delete: m("briefs.delete"),
    deleteFile: m("briefs.deleteFile"),
    deliver: m("briefs.deliver"),
    editBrief: q("briefs.editBrief"),
    getFiles: q("briefs.getFiles"),
    linkCollections: m("briefs.linkCollections"),
    list: q("briefs.list"),
    save: m("briefs.save"),
    send: m("briefs.send"),
    setInProgress: m("briefs.setInProgress"),
    uploadComplete: m("briefs.uploadComplete"),
    uploadPresign: m("briefs.uploadPresign"),
  },

  // ── collectionCategories ────────────────────────────────────────
  collectionCategories: {
    add: m("collectionCategories.add"),
    delete: m("collectionCategories.delete"),
    editCollCat: q("collectionCategories.editCollCat"),
    save: m("collectionCategories.save"),
    saveLinkOrder: m("collectionCategories.saveLinkOrder"),
    saveOrder: m("collectionCategories.saveOrder"),
  },

  // ── collections ─────────────────────────────────────────────────
  collections: {
    add: m("collections.add"),
    publish: m("collections.publish"),
    addMultiPhotos: m("collections.addMultiPhotos"),
    addPhoto: m("collections.addPhoto"),
    addToCollection: q("collections.addToCollection"),
    collectionDetails: q("collections.collectionDetails"),
    delete: m("collections.delete"),
    deleteCatLink: m("collections.deleteCatLink"),
    deleteCover: m("collections.deleteCover"),
    editCollection: q("collections.editCollection"),
    featuredList: q("collections.featuredList"),
    linkCollection: q("collections.linkCollection"),
    linkColl: m("collections.linkColl"),
    multiCollection: q("collections.multiCollection"),
    partnerCollection: q("collections.partnerCollection"),
    removePhotos: m("collections.removePhotos"),
    save: m("collections.save"),
    saveCollForResellers: m("collections.saveCollForResellers"),
    saveCollectionOrder: m("collections.saveCollectionOrder"),
    saveLove: m("collections.saveLove"),
    saveResellers: m("collections.saveResellers"),
    saveToMy: m("collections.saveToMy"),
    selectResellers: q("collections.selectResellers"),
    setFrontpage: m("collections.setFrontpage"),
    setThumb: m("collections.setThumb"),
    wizard: m("collections.wizard"),
  },

  // ── contact ──────────────────────────────────────────────────────
  contact: {
    sendMessage: m("contact.sendMessage"),
  },

  // ── cover ───────────────────────────────────────────────────────
  cover: {
    add: m("cover.add"),
    uploadComplete: m("cover.uploadComplete"),
    uploadPresign: m("cover.uploadPresign"),
  },

  // ── downloadCart ────────────────────────────────────────────────
  downloadCart: {
    addMulti: m("downloadCart.addMulti"),
    clear: m("downloadCart.clear"),
    complete: m("downloadCart.complete"),
    count: m("downloadCart.count"),
    dlDo: q("downloadCart.dlDo"),
    download: m("downloadCart.download"),
    // Browser downloader (opt-in). The four above/below are the Lambda path and are unchanged.
    startBrowserDownload: m("downloadCart.startBrowserDownload"),
    browserChunkManifest: q("downloadCart.browserChunkManifest"),
    reportBrowserChunk: m("downloadCart.reportBrowserChunk"),
    browserDownloadResume: q("downloadCart.browserDownloadResume"),
    rebuildBrowserPart: m("downloadCart.rebuildBrowserPart"),
    cancelBrowserDownload: m("downloadCart.cancelBrowserDownload"),
    downloadStatus: q("downloadCart.downloadStatus"),
    getCart: q("downloadCart.getCart"),
    ptDlDo: q("downloadCart.ptDlDo"),
    toggle: m("downloadCart.toggle"),
  },

  // ── ecommerce ───────────────────────────────────────────────────
  ecommerce: {
    abortTransfers: m("ecommerce.abortTransfers"),
    addPlugin: m("ecommerce.addPlugin"),
    addProduct: q("ecommerce.addProduct"),
    addProductImages: q("ecommerce.addProductImages"),
    addProductTemplate: m("ecommerce.addProductTemplate"),
    addVariant: m("ecommerce.addVariant"),
    addVariantData: q("ecommerce.addVariantData"),
    addVariantOption: m("ecommerce.addVariantOption"),
    bulkSetValues: q("ecommerce.bulkSetValues"),
    deletePlugin: m("ecommerce.deletePlugin"),
    deleteProduct: m("ecommerce.deleteProduct"),
    deleteVariant: m("ecommerce.deleteVariant"),
    deleteVariantOption: m("ecommerce.deleteVariantOption"),
    duplicateTemplate: m("ecommerce.duplicateTemplate"),
    ecommerce: q("ecommerce.ecommerce"),
    editVariant: q("ecommerce.editVariant"),
    refreshTransfers: q("ecommerce.transfers"),
    saveVariantOptionsOrder: m("ecommerce.saveVariantOptionsOrder"),
    saveVariantsOrder: m("ecommerce.saveVariantsOrder"),
    syncData: q("ecommerce.syncData"),
    transferQueue: q("ecommerce.transferQueue"),
    transferHistory: q("ecommerce.transferHistory"),
    transferClaim: m("ecommerce.transferClaim"),
    transferHeartbeat: m("ecommerce.transferHeartbeat"),
    transferOne: m("ecommerce.transferOne"),
    transferRelease: m("ecommerce.transferRelease"),
    transfer: m("ecommerce.transfer"),
  },

  // ── features ────────────────────────────────────────────────────
  features: {
    close: m("features.close"),
  },

  // ── follow ──────────────────────────────────────────────────────
  follow: {
    follow: m("follow.follow"),
    manageFollowed: q("follow.manageFollowed"),
    remove: m("follow.remove"),
    unfollow: m("follow.unfollow"),
  },

  // ── groups ──────────────────────────────────────────────────────
  groups: {
    addArtistGroup: m("groups.addArtistGroup"),
    addArtistsToGroups: m("groups.addArtistsToGroups"),
    addResellerGroup: m("groups.addResellerGroup"),
    addResellersToGroups: m("groups.addResellersToGroups"),
    addToArtistGroups: q("groups.addToArtistGroups"),
    artistGroups: q("groups.artistGroups"),
    manageResellerGroups: q("groups.manageResellerGroups"),
    resellerGroups: q("groups.resellerGroups"),
    removeArtistGroup: m("groups.removeArtistGroup"),
    removeResellerGroup: m("groups.removeResellerGroup"),
    saveArtistGroupMembers: m("groups.saveArtistGroupMembers"),
    saveResellerGroupMembers: m("groups.saveResellerGroupMembers"),
    selectArtistsForGroup: q("groups.selectArtistsForGroup"),
    selectResellersForGroup: q("groups.selectResellersForGroup"),
  },

  // ── incoming artworks (dashboard → artworks) ────────────────────
  incoming: {
    get: q("incoming.get"),
    list: q("incoming.list"),
    save: m("incoming.save"),
  },

  // ── lightbox ────────────────────────────────────────────────────
  lightbox: {
    addList: q("lightbox.addList"),
    addMultiPhotos: m("lightbox.addMultiPhotos"),
    addPhoto: m("lightbox.addPhoto"),
    delete: m("lightbox.delete"),
    get: q("lightbox.get"),
    multiList: q("lightbox.multiList"),
    reorderList: q("lightbox.reorderList"),
    save: m("lightbox.save"),
    saveOrder: m("lightbox.saveOrder"),
    toggleFavorite: m("lightbox.toggleFavorite"),
  },

  // ── marketing ───────────────────────────────────────────────────
  marketing: {
    delete: m("marketing.delete"),
    files: q("marketing.files"),
    matFiles: q("marketing.matFiles"),
    uploadComplete: m("marketing.uploadComplete"),
    uploadPresign: m("marketing.uploadPresign"),
  },

  // ── metadata ────────────────────────────────────────────────────
  metadata: {
    artAiSave: m("metadata.artAiSave"),
    checkTitle: q("metadata.checkTitle"),
    multiMeta: q("metadata.multiMeta"),
    queue: q("metadata.queue"),
    queueAiArtist: m("metadata.queueAiArtist"),
    saveForLater: m("metadata.saveForLater"),
    screening: q("metadata.screening"),
  },

  // ── mockupClient ────────────────────────────────────────────────
  mockupClient: {
    delete: m("mockups.ieClientDelete"),
    save: m("mockups.ieClientSave"),
  },

  // ── mockups ─────────────────────────────────────────────────────
  mockups: {
    addKeyword: m("mockups.addKeyword"),
    addKeywordMockup: q("mockups.addKeywordMockup"),
    defaultMockup: q("mockups.defaultMockup"),
    delete: m("mockups.delete"),
    deleteUser: m("mockups.deleteUser"),
    mockupsAllPreflight: q("mockups.mockupsAllPreflight"),
    mockupsAllResume: q("mockups.mockupsAllResume"),
    mockupsAllBatch: q("mockups.mockupsAllBatch"),
    reportMockupsAllBatch: m("mockups.reportMockupsAllBatch"),
    startMockupsAll: m("mockups.startMockupsAll"),
    abortMockupsAll: m("mockups.abortMockupsAll"),
    editMockup: q("mockups.editMockup"),
    editMockupUser: q("mockups.editMockupUser"),
    getAll: q("mockups.getAll"),
    ieclient: q("mockups.ieclient"),
    ieclientSelect: q("mockups.ieclientSelect"),
    ieClientRefresh: q("mockups.ieClientRefresh"),
    ieClientUploadComplete: m("mockups.ieClientUploadComplete"),
    ieClientUploadPresign: m("mockups.ieClientUploadPresign"),
    mockupAspect: q("mockups.mockupAspect"),
    mockupSelect: q("mockups.mockupSelect"),
    previewMockup: q("mockups.previewMockup"),
    previewMockup2: q("mockups.previewMockup2"),
    regenAll: m("mockups.regenAll"),
    reorderMockups: q("mockups.reorderMockups"),
    save: m("mockups.save"),
    saveCanvas: m("mockups.saveCanvas"),
    saveDefaults: m("mockups.saveDefaults"),
    saveOrder: m("mockups.saveOrder"),
    saveUser: m("mockups.saveUser"),
    selectMockup: q("mockups.selectMockup"),
    selectMockup2: q("mockups.selectMockup2"),
    toggleFav: m("mockups.toggleFav"),
    toggleSelection: m("mockups.toggleSelection"),
    uploadComplete: m("mockups.uploadComplete"),
    uploadMockupUser: q("mockups.uploadMockupUser"),
    uploadPresign: m("mockups.uploadPresign"),
  },

  // ── newsletter ──────────────────────────────────────────────────
  newsletter: {
    create: m("newsletter.create"),
    createArtist: m("newsletter.createArtist"),
    delete: m("newsletter.delete"),
    deleteArtist: m("newsletter.deleteArtist"),
    editNewsletter: q("newsletter.editNewsletter"),
    editNewsletterArtist: q("newsletter.editNewsletterArtist"),
    hide: m("newsletter.hide"),
    hideArtist: m("newsletter.hideArtist"),
    imageUploadPresign: m("newsletter.imageUploadPresign"),
    newsletters: q("newsletter.newsletters"),
    previousNewsletters: q("newsletter.previousNewsletters"),
    previousNewslettersArtist: q("newsletter.previousNewslettersArtist"),
    save: m("newsletter.save"),
    saveArtist: m("newsletter.saveArtist"),
    saveRecipients: m("newsletter.saveRecipients"),
    saveRecipientsArtist: m("newsletter.saveRecipientsArtist"),
    selectRecipients: q("newsletter.selectRecipients"),
    selectRecipientsArtist: q("newsletter.selectRecipientsArtist"),
    send: m("newsletter.send"),
    sendPrepare: m("newsletter.sendPrepare"),
    sendOne: m("newsletter.sendOne"),
    sendFinish: m("newsletter.sendFinish"),
    sendArtist: m("newsletter.sendArtist"),
    sendWelcomeEmail: m("newsletter.sendWelcomeEmail"),
    toggle: m("newsletter.toggle"),
    warnings: q("newsletter.warnings"),
  },

  // ── notifications ───────────────────────────────────────────────
  notifications: {
    hide: m("notifications.hide"),
  },

  // ── pages ───────────────────────────────────────────────────────
  pages: {
    create: m("pages.create"),
    delete: m("pages.delete"),
    editPage: q("pages.editPage"),
    list: q("pages.list"),
    save: m("pages.save"),
  },

  // ── reseller ────────────────────────────────────────────────────
  reseller: {
    download: q("reseller.download"),
    downloads: q("reseller.downloads"),
    expConfirm: m("reseller.expConfirm"),
    expConfirmData: q("reseller.expConfirmData"),
    expNotice: q("reseller.expNotice"),
    expViewer: q("reseller.expViewer"),
    expired: q("reseller.expired"),
    expiredAdmin: q("reseller.expiredAdmin"),
    invoices: q("reseller.invoices"),
    resellerDetails: q("reseller.resellerDetails"),
    resellers: q("reseller.resellers"),
    saveMetric: m("reseller.saveMetric"),
    saveResellerSettings: m("reseller.saveResellerSettings"),
    setCustomDl: m("reseller.setCustomDl"),
    setDlSettings: m("reseller.setDlSettings"),
    yourPhotos: q("reseller.yourPhotos"),
    inventoryList: q("reseller.inventoryList"),
    removeFromInventory: m("reseller.removeFromInventory"),
    inventoryImport: m("reseller.inventoryImport"),
  },

  // ── payout ──────────────────────────────────────────────────────
  payout: {
    earnings: q("payout.earnings"),
    markPaid: m("payout.markPaid"),
  },

  // ── prestatus ──────────────────────────────────────────────────
  prestatus: {
    update: m("prestatus.update"),
  },

  // ── quickDownload ───────────────────────────────────────────────
  quickDownload: {
    clear: m("quickDownload.clear"),
  },

  // ── reference ───────────────────────────────────────────────────
  reference: {
    countries: q("reference.countries"),
  },

  // ── reupload ─────────────────────────────────────────────────────
  reupload: {
    handle: m("artwork.reuploadHandle"),
  },

  // ── search ──────────────────────────────────────────────────────
  search: {
    embedTexts: m("search.embedTexts"),
    itopSearch: q("search.itopSearch"),
    load: m("search.load"),
    mapPoints: q("search.mapPoints"),
    similarArtists: q("search.similarArtists"),
    vector: m("search.vector"),
  },

  // ── users ───────────────────────────────────────────────────────
  users: {
    add: m("users.add"),
    becomeUser: m("users.becomeUser"),
    blockFromResellers: m("users.blockFromResellers"),
    changePassword: m("users.changePassword"),
    delete: m("users.delete"),
    profilePicComplete: m("users.profilePicComplete"),
    profilePicPresign: m("users.profilePicPresign"),
    resetPw: q("users.resetPw"),
    returnUser: m("users.returnUser"),
    reverseBlock: m("users.reverseBlock"),
    saveLang: m("users.saveLang"),
    saveSettings: m("users.saveSettings"),
    selectLang: q("users.selectLang"),
    settings: q("users.settings"),
  },
};
